import React from 'react';
import { Button, Form, Input, Modal, Table } from 'antd';
import { useDispatch } from 'react-redux';
import {
    createShop,
    deleteShop,
    getShops,
    TShop,
    TShopPayload,
    updateShop,
} from '../../api/setting/shops';
import { setLoading } from '../../redux/slices/appInfo';
import { useNotification } from '../../hooks/useNotification';

export const ShopsPage: React.FC = () => {
    const dispatch = useDispatch();
    const { showSuccess, showError } = useNotification();
    const [shops, setShops] = React.useState<TShop[]>([]);
    const [open, setOpen] = React.useState(false);
    const [editing, setEditing] = React.useState<TShop | null>(null);
    const [form] = Form.useForm<TShopPayload>();

    const loadShops = React.useCallback(async () => {
        try {
            dispatch(setLoading(true));
            const res = await getShops();
            if (res.status === 200) {
                setShops(res.data ?? []);
            }
        } finally {
            dispatch(setLoading(false));
        }
    }, [dispatch]);

    React.useEffect(() => {
        void loadShops();
    }, [loadShops]);

    const onOpenCreate = () => {
        setEditing(null);
        form.resetFields();
        setOpen(true);
    };

    const onOpenEdit = (shop: TShop) => {
        setEditing(shop);
        form.setFieldsValue(shop);
        setOpen(true);
    };

    const onSave = async () => {
        try {
            const values = await form.validateFields();
            dispatch(setLoading(true));
            if (editing) {
                await updateShop(editing.shopId, values);
                showSuccess('Shop updated', 'Vendor shop saved');
            } else {
                await createShop(values);
                showSuccess('Shop created', 'Vendor shop added');
            }
            setOpen(false);
            await loadShops();
        } catch {
            showError('Save failed', 'Could not save shop');
        } finally {
            dispatch(setLoading(false));
        }
    };

    const onDelete = async (shopId: number) => {
        try {
            dispatch(setLoading(true));
            await deleteShop(shopId);
            showSuccess('Shop removed', 'Vendor shop deactivated');
            await loadShops();
        } catch {
            showError('Delete failed', 'Could not remove shop');
        } finally {
            dispatch(setLoading(false));
        }
    };

    return (
        <div className="p-[24px] bg-white rounded-[8px]">
            <div className="flex justify-between items-center mb-[16px]">
                <div>
                    <h2 className="text-heading-6 font-semibold m-0">Shops</h2>
                    <p className="text-body text-mid-em m-0">
                        Third-party vendors that work on your fleet vehicles
                    </p>
                </div>
                <Button type="primary" onClick={onOpenCreate}>
                    Add shop
                </Button>
            </div>
            <Table
                rowKey="shopId"
                dataSource={shops}
                pagination={false}
                columns={[
                    { title: 'Name', dataIndex: 'name', key: 'name' },
                    { title: 'Phone', dataIndex: 'phone', key: 'phone' },
                    { title: 'Email', dataIndex: 'email', key: 'email' },
                    { title: 'Address', dataIndex: 'address', key: 'address' },
                    {
                        title: 'Actions',
                        key: 'actions',
                        render: (_, record) => (
                            <div className="flex gap-2">
                                <Button
                                    size="small"
                                    onClick={() => onOpenEdit(record)}
                                >
                                    Edit
                                </Button>
                                <Button
                                    size="small"
                                    danger
                                    onClick={() => onDelete(record.shopId)}
                                >
                                    Remove
                                </Button>
                            </div>
                        ),
                    },
                ]}
            />
            <Modal
                title={editing ? 'Edit shop' : 'Add shop'}
                open={open}
                onCancel={() => setOpen(false)}
                onOk={() => void onSave()}
                okText="Save"
            >
                <Form form={form} layout="vertical">
                    <Form.Item
                        name="name"
                        label="Shop name"
                        rules={[
                            { required: true, message: 'Name is required' },
                        ]}
                    >
                        <Input />
                    </Form.Item>
                    <Form.Item name="phone" label="Phone">
                        <Input />
                    </Form.Item>
                    <Form.Item name="email" label="Email">
                        <Input />
                    </Form.Item>
                    <Form.Item name="address" label="Address">
                        <Input />
                    </Form.Item>
                    <Form.Item name="notes" label="Notes">
                        <Input.TextArea rows={3} />
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};
